export type PeriodClosedPayload = {
  code: "PERIOD_CLOSED";
  closureIds: number[];
  protectedFromInclusive: string | null;
  protectedToInclusive: string | null;
};

type HttpErrorShape = {
  response?: {
    data?: unknown;
    status?: number;
  };
};

type FallbackScheduler = (callback: () => void) => unknown;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

export class PeriodClosedError extends Error implements PeriodClosedPayload {
  readonly code = "PERIOD_CLOSED";
  readonly status = 409;
  readonly closureIds: number[];
  readonly protectedFromInclusive: string | null;
  readonly protectedToInclusive: string | null;
  readonly originalError: unknown;
  private handled = false;

  constructor(payload: PeriodClosedPayload, originalError: unknown) {
    super("PERIOD_CLOSED");
    this.name = "PeriodClosedError";
    this.closureIds = [...payload.closureIds];
    this.protectedFromInclusive = payload.protectedFromInclusive;
    this.protectedToInclusive = payload.protectedToInclusive;
    this.originalError = originalError;
  }

  claim(): boolean {
    if (this.handled) return false;
    this.handled = true;
    return true;
  }
}

export function isPeriodClosedError(error: unknown): error is PeriodClosedError {
  return error instanceof PeriodClosedError;
}

export function normalizeHttpError(error: unknown): unknown {
  if (isPeriodClosedError(error)) return error;
  const response = asRecord(asRecord(error)?.response);
  const data = asRecord(response?.data);
  if (response?.status !== 409 || data?.code !== "PERIOD_CLOSED") return error;
  return new PeriodClosedError({
    code: "PERIOD_CLOSED",
    closureIds: Array.isArray(data.closureIds)
      ? data.closureIds.filter((id): id is number => typeof id === "number")
      : [],
    protectedFromInclusive: typeof data.protectedFromInclusive === "string"
      ? data.protectedFromInclusive
      : null,
    protectedToInclusive: typeof data.protectedToInclusive === "string"
      ? data.protectedToInclusive
      : null,
  }, error);
}

export function claimPeriodClosedError(error: unknown): PeriodClosedError | null {
  const normalized = normalizeHttpError(error);
  return isPeriodClosedError(normalized) && normalized.claim() ? normalized : null;
}

export function deferPeriodClosedFallback(
  error: unknown,
  notify: (error: PeriodClosedError) => void,
  schedule: FallbackScheduler = (callback) => window.setTimeout(callback, 0),
): unknown {
  const normalized = normalizeHttpError(error);
  if (!isPeriodClosedError(normalized)) return normalized;
  schedule(() => {
    const claimed = claimPeriodClosedError(normalized);
    if (claimed) notify(claimed);
  });
  return normalized;
}

export function getClientHttpErrorMessage(
  error: unknown,
  translate: (key: string) => string,
  fallback: string,
): string {
  if (claimPeriodClosedError(error)) {
    return translate("wkt.client.period-closed.mutation_conflict");
  }
  const data = (error as HttpErrorShape)?.response?.data;
  const serviceMessage = asRecord(data)?.message;
  if (typeof serviceMessage === "string" && serviceMessage.trim()) return serviceMessage;
  return error instanceof Error && error.message ? error.message : fallback;
}
